import React, { useEffect } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Navigate, useOutlet } from "react-router-dom";
import Sidebar from "components/Sidebar";
import Navbar from "components/Navbar";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "../../contexts/AuthContext";
import { useVersionCheck } from "hooks/useVersionCheck";
import UpdateNotification from "components/UpdateNotification";
import { NAVBAR_HEIGHT } from "constants/layout";

const Layout = () => {
  const { userLoggedIn } = useAuth();
  const { currentUser } = useAuth();

  const outlet = useOutlet();
  const { updateAvailable, refreshPage } = useVersionCheck();

  const isNonMobile = useMediaQuery("(min-width: 600px)");

  // Use the context for sidebar state management
  const {
    activeMenu,
    setActiveMenu,
    screenSize,
    setScreenSize,
    isCollapsed,
  } = useStateContext();

  // Define drawer widths
  const drawerWidth = 288;
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25); // 25% of full width

  // Calculate current drawer width based on collapsed state
  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [setScreenSize]);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize, setActiveMenu]);

  if (!userLoggedIn) return <Navigate to="/" replace />;
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Sidebar */}
      <Sidebar
        user={currentUser}
        isNonMobile={isNonMobile}
        drawerWidth={drawerWidth}
      />

      {/* Main Content Container */}
      <Box
        sx={{
          flexGrow: 1,
          width:
            isNonMobile && activeMenu
              ? `calc(100% - ${currentDrawerWidth}px)`
              : "100%",

          ml: isNonMobile && activeMenu ? `${currentDrawerWidth}px` : 0,
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          transition: (theme) =>
            theme.transitions.create(["width", "margin-left"], {
              easing: theme.transitions.easing.easeInOut,
              duration: 1000, // 1000ms animation
            }),
          bgcolor: "background.default",
        }}
      >
        {/* Navbar - Fixed at top */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            height: NAVBAR_HEIGHT,
            minHeight: NAVBAR_HEIGHT,
            bgcolor: "background.default",
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Navbar user={currentUser} />
        </Box>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
            }}
          >
            <UpdateNotification
              open={updateAvailable}
              onRefresh={refreshPage}
            />
            <Box>{outlet}</Box>
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              height: "20px",
              minHeight: "20px",
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: (theme) => theme.palette.background.default,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
